import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { useAuthPrompt, useLikeAuthGate } from '../components/AuthPromptModal';
import { Heart, MessageCircle, Share2, Music2, UserPlus, Play, Volume2, VolumeX, ChevronLeft, Home, Library, ThumbsDown, ListMusic } from 'lucide-react';
import { toast } from 'sonner';
import { CommentModal } from '../components/CommentSection';
import { AddToPlaylistModal } from '../components/AddToPlaylistModal';
import { PulsePreroll } from '../components/PulsePreroll';
import { shareVideo } from '../utils/share';
import { AIDisclosureBadges } from '../components/AIDisclosure';
import { FoundingCreatorBadge } from '../components/FoundingCreatorBadge';
import { FounderBadge } from '../components/FounderBadge';
import { resolvePlaybackUrl, useHlsVideo, resolveThumbnailUrl } from '../utils/hlsPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

const GENRE_THUMBS = {
  'Hip-Hop / Rap': "https://images.unsplash.com/photo-1771310961652-1af9711686ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxoaXAlMjBob3AlMjByYXBwZXIlMjBzdHVkaW8lMjBkYXJrfGVufDB8fHx8MTc3NTE4Njc1MHww&ixlib=rb-4.1.0&q=85",
  'Jazz': "https://images.pexels.com/photos/359995/pexels-photo-359995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Rock': "https://images.unsplash.com/photo-1772597228086-1f93b8da792d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxyb2NrJTIwY29uY2VydCUyMGVsZWN0cmljJTIwZ3VpdGFyJTIwZGFya3xlbnwwfHx8fDE3NzUxODY3NTB8MA&ixlib=rb-4.1.0&q=85",
  'R&B / Soul': "https://images.unsplash.com/photo-1774016591255-10545a911cb2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxSJTI2QiUyMHNvdWwlMjBzaW5nZXIlMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc3NTE4Njc1MHww&ixlib=rb-4.1.0&q=85",
  'Electronic / Dance (EDM)': "https://images.unsplash.com/photo-1701705319364-ec5468034cf6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljJTIwREolMjB0dXJudGFibGUlMjBkYXJrJTIwY2x1YnxlbnwwfHx8fDE3NzUxODY3NjN8MA&ixlib=rb-4.1.0&q=85",
  'Pop': "https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwb3AlMjBtdXNpYyUyMHNpbmdlciUyMHBlcmZvcm1hbmNlJTIwY29sb3JmdWwlMjBsaWdodHN8ZW58MHx8fHwxNzc1MTg2Nzc0fDA&ixlib=rb-4.1.0&q=85",
  'Latin': "https://images.pexels.com/photos/13543306/pexels-photo-13543306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Country': "https://images.pexels.com/photos/164729/pexels-photo-164729.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Indie / Alternative': "https://images.pexels.com/photos/9454717/pexels-photo-9454717.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'K-Pop': "https://images.unsplash.com/photo-1526218626217-dc65a29bb444?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwb3AlMjBtdXNpYyUyMHNpbmdlciUyMHBlcmZvcm1hbmNlJTIwY29sb3JmdWwlMjBsaWdodHN8ZW58MHx8fHwxNzc1MTg2Nzc0fDA&ixlib=rb-4.1.0&q=85",
  'Afrobeats': "https://images.pexels.com/photos/13543306/pexels-photo-13543306.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  'Classical': "https://images.pexels.com/photos/359995/pexels-photo-359995.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const DEMO_VIDEOS = [];

const formatCount = (n) => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n;
};

// Single Pulse video card - full screen TikTok style with autoplay
const PulseCard = ({ video, isActive, isNear, onLike, onShare, onArtistClick, onFollow, onNotInterested, onComment, onPlaylist, getThumbnail, getVideoUrl, index, globalMuted, onToggleMute, onAutoplayMuted }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const requireAuthForLike = useLikeAuthGate();
  const [liked, setLiked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef(null);
  const hasVideo = video.file_path || video.file_url || video.video_path || video.hls_url;
  // Only attach src when the card is near the viewport (the swipe-feed
  // hydrate-on-approach pattern). Passing null keeps the hook idle.
  useHlsVideo(videoRef, isNear ? getVideoUrl(video) : null, { autoStart: false });

  // Hook window — Pulse is a trailer surface, not a player. Each card plays
  // the strongest 20-40s of the track on loop and pushes users to the main
  // player for full playback.
  const hookStart = Number.isFinite(video.hook_start) ? Math.max(0, video.hook_start) : 0;
  const hookDuration = Number.isFinite(video.hook_duration) ? Math.max(20, Math.min(40, video.hook_duration)) : 30;
  const hookEnd = hookStart + hookDuration;

  // Autoplay when card becomes active. globalMuted is intentionally NOT a dep —
  // toggling mute should not restart the video from 0 (and the catch path below
  // would otherwise force-mute again).
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !hasVideo) return;
    // NOTE: do NOT guard on `videoEl.src` here. When the source is an HLS
    // stream attached via hls.js, the element binds to a MediaSource (blob:
    // URL) ASYNCHRONOUSLY — `videoEl.src` is empty for ~50-200ms after the
    // hook attaches. Returning early during that window prevents play()
    // from ever firing and produces the "black screen on Pulse" symptom.
    //
    // Log the URL we're trying to play (one-time per card) so production
    // misconfigurations (empty/malformed URLs) are visible in the console.
    if (isActive) {
      // eslint-disable-next-line no-console
      console.debug('[Pulse] playing video', {
        id: video.id, title: video.title, status: video.status,
        url: getVideoUrl(video), hasHls: !!video.hls_url,
      });
    }

    // Cancel flag in case the user swipes away before play()/catch resolves.
    // Without this, the unmounted-card's video can still flip back to
    // playing AFTER the user has moved on — accumulating background
    // streams that eventually freeze the tab.
    let cancelled = false;

    if (isActive) {
      try {
        if (videoEl.duration && hookStart < videoEl.duration) {
          videoEl.currentTime = hookStart;
        } else {
          videoEl.currentTime = 0;
        }
      } catch {
        videoEl.currentTime = 0;
      }
      videoEl.muted = globalMutedRef.current;
      const playPromise = videoEl.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => { if (!cancelled) setIsPlaying(true); })
          .catch(() => {
            if (cancelled) return;
            // Browser blocked autoplay-with-sound (typical on iOS Safari +
            // Chrome until the user has gestured on the page). Fall back to
            // muted autoplay so the video still plays, AND flip the global
            // mute state to true so the speaker icon reflects reality.
            videoEl.muted = true;
            videoEl.play()
              .then(() => {
                if (cancelled) return;
                setIsPlaying(true);
                if (!globalMutedRef.current && typeof onAutoplayMuted === 'function') {
                  onAutoplayMuted();
                }
              })
              .catch(() => { if (!cancelled) setIsPlaying(false); });
          });
      }
    } else {
      videoEl.pause();
      setIsPlaying(false);
    }

    return () => {
      cancelled = true;
      // Belt-and-suspenders: hard-pause whenever this effect tears down
      // so adjacent cards never keep audio bleeding through. Suppressed
      // because iOS Safari can throw on pause() during element teardown.
      try { videoEl.pause(); } catch (_e) { /* element already removed */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, hasVideo, hookStart]);

  // Race-safety for HLS streams: when hls.js attaches the manifest AFTER the
  // card became active, `loadedmetadata` fires once the media is ready. Trigger
  // play() then so a delayed manifest parse doesn't leave us on a black screen.
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    const onLoadedMeta = () => {
      if (!isActive) return;
      try {
        if (hookStart > 0 && videoEl.duration && hookStart < videoEl.duration) {
          videoEl.currentTime = hookStart;
        }
        videoEl.play().catch(() => {
          videoEl.muted = true;
          videoEl.play().catch(() => { /* user-gesture required */ });
        });
      } catch { /* element teardown */ }
    };
    videoEl.addEventListener('loadedmetadata', onLoadedMeta);
    return () => videoEl.removeEventListener('loadedmetadata', onLoadedMeta);
  }, [isActive, hookStart]);

  // Keep a live ref of globalMuted so the autoplay effect picks up the latest
  // value without re-firing.
  const globalMutedRef = useRef(globalMuted);
  useEffect(() => {
    globalMutedRef.current = globalMuted;
    if (videoRef.current) {
      videoRef.current.muted = globalMuted;
    }
  }, [globalMuted]);

  // Progress tracking + hook-window loop
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    const handleTimeUpdate = () => {
      if (!videoEl.duration) return;
      const effectiveEnd = Math.min(hookEnd, videoEl.duration);
      // If we drift past the hook window, loop back to hook start
      if (effectiveEnd > hookStart && videoEl.currentTime >= effectiveEnd) {
        try { videoEl.currentTime = hookStart; } catch { /* noop */ }
      }
      // Progress bar shows position WITHIN the hook window so the swipe-trailer
      // feels predictable (full bar = full hook played)
      const span = Math.max(0.5, effectiveEnd - hookStart);
      const local = Math.max(0, Math.min(span, videoEl.currentTime - hookStart));
      setProgress((local / span) * 100);
    };

    const handleLoadedMetadata = () => {
      setDuration(videoEl.duration);
      // Defensively re-seek to hook start once we know real duration
      if (isActive && videoEl.duration && hookStart < videoEl.duration) {
        try { videoEl.currentTime = hookStart; } catch { /* noop */ }
      }
    };

    const handleEnded = () => {
      // Reached real end of file (hook overlapped end) → loop hook
      try { videoEl.currentTime = hookStart; } catch { /* noop */ }
      videoEl.play();
    };

    videoEl.addEventListener('timeupdate', handleTimeUpdate);
    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('ended', handleEnded);

    return () => {
      videoEl.removeEventListener('timeupdate', handleTimeUpdate);
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('ended', handleEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bind listeners once per mounted card; hook bounds are fixed per video
  }, []);

  const handleLike = () => {
    // Gate runs BEFORE the optimistic flip so a logged-out user never sees
    // a half-filled heart. Same shared modal as every other like surface.
    if (!requireAuthForLike(user, video?.id)) return;
    setLiked(!liked);
    onLike(video, !liked);
  };

  const togglePlayPause = () => {
    const videoEl = videoRef.current;
    if (!videoEl || !hasVideo) return;

    // No on-screen play/pause overlay on tap — TikTok-clean.
    // The progress bar + spinning music disc already convey play state.
    if (isPlaying) {
      videoEl.pause();
      setIsPlaying(false);
    } else {
      videoEl.play();
      setIsPlaying(true);
    }
  };

  // Only render the <video> element when we have a real, non-empty URL.
  // Passing null/empty src to <video> triggers "no supported sources" errors.
  const videoUrl = getVideoUrl(video);
  const thumbnailUrl = getThumbnail(video, index);
  const canPlayVideo = hasVideo && !!videoUrl;

  return (
    <div 
      className="relative w-full h-full flex-shrink-0"
      data-testid={`pulse-card-${video.id}`}
    >
      {/* Background - Video or Image */}
      <div className="absolute inset-0 bg-black">
        {canPlayVideo ? (
          <>
            {/* Actual video element — only buffer aggressively for the
                active card. Adjacent cards preload metadata only. Far
                cards don't fetch the file at all, preventing iOS Safari
                from freezing under 20+ <video> elements all buffering
                ~30s of 720p simultaneously. */}
            <video
              ref={videoRef}
              poster={thumbnailUrl}
              className="w-full h-full object-cover"
              loop
              muted={globalMuted}
              playsInline
              webkit-playsinline="true"
              preload={isActive ? 'auto' : isNear ? 'metadata' : 'none'}
              data-testid={`pulse-video-${video.id}`}
            />
          </>
        ) : (
          /* Fallback thumbnail for demo videos */
          <img
            src={thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover"
          />
        )}
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
      </div>

      {/* Progress bar at bottom */}
      {hasVideo && (
        <div
          className="absolute left-0 right-0 h-1 bg-white/20 z-30"
          style={{ bottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div 
            className="h-full bg-white transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Play/Pause tap area — no on-screen icon overlay. The progress bar
          + spinning music disc on the right rail already indicate playback
          state. Keeps the Pulse feed visually clean like TikTok. */}
      <button 
        className="absolute inset-0 z-10"
        onClick={togglePlayPause}
        data-testid={`pulse-play-${video.id}`}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {/* Persistent play icon only when there is no video to play (demo
            fallback / preview cards). Real, playing videos render nothing. */}
        {!hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Play className="w-10 h-10 text-white fill-white ml-1" />
            </div>
          </div>
        )}
      </button>

      {/* Right side action buttons */}
      <div
        className="absolute right-4 flex flex-col items-center gap-6 z-20"
        style={{ bottom: 'calc(8.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        {/* Artist avatar */}
        <div 
          className="relative cursor-pointer"
          onClick={() => onArtistClick(video)}
          data-testid={`pulse-avatar-${video.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-zinc-700 border-2 border-white flex items-center justify-center overflow-hidden">
            {video.artist_avatar ? (
              <img src={`${API}/api/files/${video.artist_avatar}`} alt={video.artist} className="w-full h-full object-cover" />
            ) : (
              <span className="text-lg font-bold text-white">{(video.artist || '?').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div 
            className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onFollow(video); }}
            data-testid={`pulse-follow-${video.id}`}
          >
            <UserPlus className="w-3.5 h-3.5 text-white" />
          </div>
        </div>

        {/* Like */}
        <button 
          className="flex flex-col items-center gap-1"
          onClick={handleLike}
          data-testid={`pulse-like-${video.id}`}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${liked ? 'bg-red-500/20 scale-110' : 'bg-white/10 backdrop-blur-sm'}`}>
            <Heart className={`w-7 h-7 transition-all ${liked ? 'text-red-500 fill-red-500 scale-110' : 'text-white'}`} />
          </div>
          <span className="text-xs text-white font-medium">{formatCount((video.likes || 0) + (liked ? 1 : 0))}</span>
        </button>

        {/* Comment */}
        <button 
          className="flex flex-col items-center gap-1"
          onClick={() => onComment && onComment(video)}
          data-testid={`pulse-comment-${video.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <MessageCircle className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs text-white font-medium">Comments</span>
        </button>

        {/* Share */}
        <button 
          className="flex flex-col items-center gap-1"
          onClick={() => onShare(video)}
          data-testid={`pulse-share-${video.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <Share2 className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs text-white font-medium">Share</span>
        </button>

        {/* Add to Playlist */}
        <button 
          className="flex flex-col items-center gap-1"
          onClick={() => onPlaylist && onPlaylist(video)}
          data-testid={`pulse-playlist-${video.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
            <ListMusic className="w-7 h-7 text-white" />
          </div>
          <span className="text-xs text-white font-medium">Playlist</span>
        </button>

        {/* Not Interested */}
        <button 
          className="flex flex-col items-center gap-1"
          onClick={() => onNotInterested(video)}
          data-testid={`pulse-not-interested-${video.id}`}
        >
          <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-zinc-500/30 transition-colors">
            <ThumbsDown className="w-6 h-6 text-white" />
          </div>
          <span className="text-xs text-white/70 font-medium">Not for me</span>
        </button>

        {/* Music disc - spins when playing */}
        <div className={`w-12 h-12 rounded-full bg-zinc-900 border-2 border-zinc-600 flex items-center justify-center ${isPlaying ? 'animate-spin-slow' : ''}`}>
          <Music2 className="w-5 h-5 text-white" />
        </div>
      </div>

      {/* Bottom info */}
      <div
        className="absolute left-4 right-20 z-20"
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <button 
          className="flex items-center gap-2 mb-2"
          onClick={() => onArtistClick(video)}
          data-testid={`pulse-artist-${video.id}`}
        >
          <span className="text-base font-bold text-white">@{(video.artist || video.username || 'unknown')?.replace(/\s+/g, '_').toLowerCase()}</span>
          {video.creator_tier && (
            <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ background: `${video.creator_tier.color}40`, color: video.creator_tier.color }}>
              {video.creator_tier.badge}
            </span>
          )}
          <FoundingCreatorBadge number={video.creator_tier?.founding_number} />
          {video.creator_tier?.is_founder && <FounderBadge size="sm" />}
        </button>
        <h3 className="text-lg font-semibold text-white mb-1">{video.title}</h3>
        {(video.caption || video.description) && (
          <p className="text-sm text-white/80 line-clamp-2">{video.caption || video.description}</p>
        )}

        {/* "Made with" tool badges — discovery loop entry on the feed */}
        <AIDisclosureBadges
          disclosure={video.ai_disclosure}
          onTap={(toolKey) => navigate(`/tools/${toolKey}`)}
          dense
          className="mt-2"
        />

        {/* HOOK reel CTA — Pulse is the trailer, this is the path to the full song */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/video/${video.id}?autoplay=1`);
          }}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-semibold shadow-lg active:scale-95 transition-transform"
          data-testid={`pulse-play-full-${video.id}`}
        >
          <Play className="w-4 h-4 fill-black" />
          Play full song
        </button>
        
        {/* Music ticker */}
        <div className="flex items-center gap-2 mt-3">
          <Music2 className="w-4 h-4 text-white flex-shrink-0" />
          <div className="overflow-hidden flex-1">
            <p className={`text-sm text-white whitespace-nowrap ${isPlaying ? 'animate-marquee' : ''}`}>
              {video.title} - {video.artist || video.username} &nbsp;&nbsp;&nbsp; {video.title} - {video.artist || video.username}
            </p>
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              // Synchronously unmute on the video element so iOS keeps the
              // user-gesture context (state-driven unmute is too late on iOS).
              const videoEl = videoRef.current;
              const next = !globalMuted;
              if (videoEl) {
                videoEl.muted = next;
                if (!next) {
                  // Resume audio playback right inside the gesture
                  videoEl.play().catch(() => { /* best-effort, non-blocking */ });
                }
              }
              onToggleMute();
            }}
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
              globalMuted 
                ? 'bg-red-500/30 border-red-500/50 animate-pulse' 
                : 'bg-white/10 border-white/20'
            }`}
            data-testid="pulse-mute-btn"
          >
            {globalMuted ? <VolumeX className="w-4 h-4 text-red-400" /> : <Volume2 className="w-4 h-4 text-white" />}
          </button>
        </div>
      </div>

      {/* No video indicator for demo content */}
      {!hasVideo && (
        <div className="absolute top-16 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full">
          <span className="text-xs text-white/70">Preview • Upload to add video</span>
        </div>
      )}
    </div>
  );
};

export const PulsePage = () => {
  const [videos, setVideos] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  // Default to UNMUTED so the first video plays with audio when the browser
  // allows it. iOS Safari + Chrome Mobile typically reject autoplay-with-sound
  // before any user gesture — the PulseCard autoplay effect catches that
  // rejection, falls back to muted playback, and calls onAutoplayMuted() to
  // flip this state to `true` so the speaker icon honestly shows the mute state.
  const [globalMuted, setGlobalMuted] = useState(false);
  const [commentVideoId, setCommentVideoId] = useState(null);
  const [playlistVideoId, setPlaylistVideoId] = useState(null);
  const [showPreroll, setShowPreroll] = useState(() => PulsePreroll.shouldShow());
  // Anonymous Pulse soft prompt — dismissable, persistence in localStorage so
  // we don't nag the same anon user every visit.
  const [showAnonPrompt, setShowAnonPrompt] = useState(() => {
    try {
      return localStorage.getItem('aivue.pulseAnonPromptDismissed') !== '1';
    } catch {
      return true;
    }
  });
  const { user, axiosInstance, token } = useAuth();
  const { promptAuth } = useAuthPrompt();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  useEffect(() => {
    fetchVideos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVideos = async () => {
    try {
      // Pulse passes `pulse=true` so the backend can decide:
      // - while platform has <PULSE_DIVERSITY_THRESHOLD (100) approved videos:
      //   force per-creator diversity (≤2 per creator) so one prolific
      //   artist doesn't dominate everyone's vertical scroll.
      // - once catalog exceeds that, backend flips back to algorithmic /
      //   personalized mode (no diversity cap, variety from sheer pool size).
      const res = await axiosInstance.get('/videos', { params: { limit: 20, pulse: true, diversify: false } });
      const real = res.data || [];
      const realIds = new Set(real.map(v => v.id));
      // Merge real + demo, but prioritize real videos first
      const combined = [...real, ...DEMO_VIDEOS.filter(d => !realIds.has(d.id))];
      // Shuffle only the demo videos portion. A "real" video has EITHER a
      // legacy storage path (file_path/file_url/video_path) OR a Bunny HLS
      // URL — without `hls_url` in this predicate, freshly-uploaded Bunny
      // videos would be mis-bucketed as demos and never play.
      const hasPlayable = (v) =>
        !!(v.file_path || v.file_url || v.video_path || v.hls_url);
      const realVideos = combined.filter(hasPlayable);
      const demoVideos = combined.filter((v) => !hasPlayable(v));
      for (let i = demoVideos.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [demoVideos[i], demoVideos[j]] = [demoVideos[j], demoVideos[i]];
      }
      // Real videos first, then shuffled demos
      setVideos([...realVideos, ...demoVideos]);
    } catch (err) {
      console.error('Pulse fetch error:', err);
      setVideos(DEMO_VIDEOS);
    } finally {
      setLoading(false);
    }
  };

  const getThumbnail = (video, index) => {
    const genre = video?.genre || 'Pop';
    return resolveThumbnailUrl(video, GENRE_THUMBS[genre] || GENRE_THUMBS['Pop']);
  };

  const getVideoUrl = (video) => {
    // Prefer Bunny HLS when available; fall back to legacy /api/files paths.
    const url = resolvePlaybackUrl(video);
    if (url) return url;
    if (video?.file_path) return `${API}/api/files/${video.file_path}`;
    if (video?.video_path) return `${API}/api/files/${video.video_path}`;
    if (video?.file_url) return video.file_url;
    return null;
  };

  // Continuous scroll (iter 122). Fetching more videos when the user
  // approaches the end of the current list. Because the /videos endpoint
  // in `pulse=true` mode returns a fresh quality-weighted random shuffle
  // on every call, subsequent pages are guaranteed to be different from
  // page 1 — no need to track pagination cursors, just fetch another
  // batch and append. `isFetchingMore` prevents overlapping fetches when
  // the user scrolls quickly.
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchMoreVideos = useCallback(async () => {
    if (isFetchingMore) return;
    setIsFetchingMore(true);
    try {
      const res = await axiosInstance.get('/videos', { params: { limit: 20, pulse: true, diversify: false } });
      const incoming = res.data || [];
      // De-dup against what we already have so consecutive fetches don't
      // stack the same video twice (server randomness overlaps by design).
      setVideos((prev) => {
        const have = new Set(prev.map((v) => v.id));
        const additions = incoming.filter((v) => v && v.id && !have.has(v.id));
        return additions.length ? [...prev, ...additions] : prev;
      });
    } catch (_e) {
      // Fetch failure is non-fatal; user still has whatever's already loaded.
    } finally {
      setIsFetchingMore(false);
    }
  }, [axiosInstance, isFetchingMore]);

  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.clientWidth;
    if (cardWidth === 0) return;
    const newIndex = Math.round(scrollLeft / cardWidth);
    if (newIndex !== activeIndex && newIndex >= 0 && newIndex < videos.length) {
      setActiveIndex(newIndex);
    }
    // Prefetch a new batch when the viewer is within 3 cards of the end.
    // Threshold picked to give the fetch enough time to complete before
    // the user actually reaches the last card, so the scroll feels seamless.
    if (videos.length > 0 && newIndex >= videos.length - 3) {
      fetchMoreVideos();
    }
  }, [activeIndex, videos.length, fetchMoreVideos]);

  // Record view when video becomes active. Depend ONLY on the active
  // video's id — using the full `videos` array would re-fire on every
  // array reference change (re-fetch, like, not-interested) and emit
  // duplicate /view POSTs that pile up and freeze the tab.
  const activeVideoId = videos[activeIndex]?.id;
  useEffect(() => {
    if (activeVideoId) {
      axiosInstance.post(`/videos/${activeVideoId}/view`).catch(() => { /* best-effort, non-blocking */ });
    }
  }, [activeVideoId, axiosInstance]);

  const handleLike = async (video, isLiked) => {
    if (!user) { promptAuth('Sign up to like videos and save your favorites.'); return; }
    try {
      await axiosInstance.post(`/videos/${video.id}/like`);
      toast.success(isLiked ? 'Added to favorites!' : 'Removed from favorites');
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error('Failed to like video:', err);
      }
    }
  };

  const handleShare = (video) => {
    shareVideo(video);
  };

  const handleArtistClick = (video) => {
    const artistId = video.user_id || video.artist_id || encodeURIComponent(video.artist || 'unknown');
    navigate(`/creator/${artistId}`);
  };

  const handleFollow = async (video) => {
    if (!user) { promptAuth('Sign up to follow your favorite creators.'); return; }
    toast.success('Following!');
  };

  const handleNotInterested = async (video) => {
    if (!user) { promptAuth('Sign up to personalize your feed.'); return; }
    try {
      await axiosInstance.post(`/videos/${video.id}/not-interested`);
      setVideos(prev => prev.filter(v => v.id !== video.id));
      toast.success("Got it! We'll show you less like this");
    } catch {
      toast.error('Failed to update preferences');
    }
  };

  const toggleGlobalMute = () => {
    setGlobalMuted(!globalMuted);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {showPreroll && (
        <PulsePreroll
          firstVideo={videos[0]}
          onDone={() => {
            PulsePreroll.markSeen();
            setShowPreroll(false);
          }}
        />
      )}
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-black flex flex-row pulse-scroll-container"
      onScroll={handleScroll}
      style={{ 
        overflowX: 'auto', 
        overflowY: 'hidden',
        scrollSnapType: 'x mandatory', 
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-x',
        overscrollBehaviorX: 'contain'
      }}
      data-testid="pulse-feed"
    >
      {/* Floating Navigation Header */}
      <div
        className="fixed top-0 left-0 right-0 z-50 px-4 py-3 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent pointer-events-none"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm"
            data-testid="pulse-back-btn"
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm"
            data-testid="pulse-home-btn"
          >
            <Home className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={() => navigate('/library')}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm"
            data-testid="pulse-library-btn"
          >
            <Library className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Anonymous Pulse soft prompt — sticky chip below floating header,
          dismissable; persists in localStorage so we don't nag every visit. */}
      {!user && showAnonPrompt && (
        <div
          className="fixed top-0 left-0 right-0 z-40 px-4 flex justify-center pointer-events-none"
          style={{ paddingTop: 'calc(3.75rem + env(safe-area-inset-top, 0px))' }}
          data-testid="pulse-anon-prompt"
        >
          <div className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 backdrop-blur shadow-lg max-w-[92vw]">
            <span className="text-[12px] font-medium text-black truncate">
              Sign up to personalize your Pulse
            </span>
            <button
              onClick={() => navigate(`/register?next=${encodeURIComponent('/pulse')}`)}
              className="text-[12px] font-semibold text-orange-600 hover:text-orange-700 px-2 py-0.5 rounded-full whitespace-nowrap"
              data-testid="pulse-anon-prompt-signup"
            >
              Sign up →
            </button>
            <button
              onClick={() => {
                try { localStorage.setItem('aivue.pulseAnonPromptDismissed', '1'); } catch { /* noop */ }
                setShowAnonPrompt(false);
              }}
              className="text-zinc-500 hover:text-black p-1 flex items-center justify-center text-base leading-none"
              aria-label="Dismiss"
              data-testid="pulse-anon-prompt-dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {videos.map((video, index) => {
        // Only the active card + 1 prev + 1 next have a real video src
        // attached. Everything else renders just the poster image. Keeps
        // iOS Safari from buffering 20 streams in parallel and freezing.
        const isActive = index === activeIndex;
        const isNear = Math.abs(index - activeIndex) <= 1;
        return (
        <div key={video.id} style={{ minWidth: '100vw', height: '100dvh', scrollSnapAlign: 'start', scrollSnapStop: 'always' }} className="flex-shrink-0">
          <PulseCard
            video={video}
            index={index}
            isActive={isActive}
            isNear={isNear}
            onLike={handleLike}
            onShare={handleShare}
            onArtistClick={handleArtistClick}
            onFollow={handleFollow}
            onNotInterested={handleNotInterested}
            onComment={(video) => {
              // Use the iter-29 comment-specific copy so anon Pulse users see
              // the same "Sign in to join the conversation" prompt as
              // /video/{id} — consistency across surfaces.
              if (!user) {
                promptAuth({
                  title: 'Sign in to join the conversation',
                  body: "Your comments save under your profile and help creators see who's tuned in.",
                  primaryButton: 'Sign in',
                  secondaryButton: 'Create an account',
                  primaryPath: '/login',
                });
                return;
              }
              setCommentVideoId(video.id);
            }}
            onPlaylist={(video) => { if (!user) { promptAuth('Sign up to create playlists and save videos.'); return; } setPlaylistVideoId(video.id); }}
            getThumbnail={getThumbnail}
            getVideoUrl={getVideoUrl}
            globalMuted={globalMuted}
            onToggleMute={toggleGlobalMute}
            onAutoplayMuted={() => setGlobalMuted(true)}
          />
        </div>
        );
      })}
      
      {/* Comment Modal */}
      <CommentModal
        videoId={commentVideoId}
        isOpen={!!commentVideoId}
        onClose={() => setCommentVideoId(null)}
      />

      {/* Add to Playlist Modal */}
      <AddToPlaylistModal
        videoId={playlistVideoId}
        isOpen={!!playlistVideoId}
        onClose={() => setPlaylistVideoId(null)}
      />
    </div>
    </>
  );
};
